import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "@/redux/store";
import { useSelector } from "react-redux";

import { Button, Modal, ChangePasswordModal, SuccessSaveModal, NameInput, EmailInput, PhoneInput, ConfirmPhoneModal } from "@/components";
import { getProfileInfo } from "@/redux/profile/settings/getProfileInfo/selectors";
import { fetchProfileInfo } from "@/redux/profile/settings/getProfileInfo/asyncActions";
import { fetchUpdateUserInfo } from "@/redux/profile/settings/updateUserInfo/asyncActions";
import { fetchDeleteAccount } from "@/redux/profile/settings/deleteAccount/asyncActions";
import { resetCartItems } from "@/redux/cart/slice";
import { setAuth } from "@/redux/profile/authAndReg/slice";
import { fetchLogout } from "@/redux/profile/authAndReg/asyncActions";
import {
  removeTokenToStorage,
  removePartnerIdFromStorage,
  removeCartItemsFromStorage,
} from "@/utils";
import { phoneMaskFormat } from "@/utils/form/phoneMask";
import { ValidMessages } from "@/resources/locale";
import arrowIcon from "@/assets/icons/arrowCalendarLeft.svg";
import { useCurrentWidth } from "@/hooks";
import { MediaBreakPoints } from "@/resources/constants";
import { useNavigate } from "react-router-dom";

import styles from "./Settings.module.scss";

const normalizePhone = (phoneNumber: string | undefined): string => {
  if (!phoneNumber) return "";
  return phoneNumber.replace(/\D/g, "");
};

export const Settings: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const windowWidth = useCurrentWidth();
  const isMobile = windowWidth <= MediaBreakPoints.DESKTOP;

  const { profileInfo } = useSelector(getProfileInfo);
  const [isOpenPassModal, setIsOpenPassModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isPhoneConfirmModalOpen, setIsPhoneConfirmModalOpen] = useState<boolean>(false);
  const [pendingPhone, setPendingPhone] = useState<string>("");
  const [pendingFormValues, setPendingFormValues] = useState<{ name: string; email: string; phone: string } | null>(null);

  useEffect(() => {
    dispatch(fetchProfileInfo());
  }, [dispatch]);

  const handleChangePass = () => {
    setIsOpenPassModal(true);
  };

  const handleBack = () => {
    navigate('/profile');
  };

  const handleDeleteAccount = async () => {
    try {
      await dispatch(fetchDeleteAccount()).unwrap();
      
      await dispatch(fetchLogout());
      
      dispatch(resetCartItems());
      dispatch(setAuth(null));
      removeTokenToStorage();
      removePartnerIdFromStorage();
      removeCartItemsFromStorage();
      
      setIsDeleteModalOpen(false);
      window.location.href = "/";
    } catch (error: any) {
      console.error("Ошибка при удалении аккаунта:", error);
      alert(error.message || "Ошибка при удалении аккаунта");
    }
  };

  const initialValues = {
    name: profileInfo?.name || "",
    email: profileInfo?.email || "",
    phone: profileInfo?.phone ? phoneMaskFormat(profileInfo.phone) : "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(ValidMessages.inputs.name.REQUIRED),
    email: Yup.string()
      .email(ValidMessages.inputs.email.INCORRECT)
      .required(ValidMessages.inputs.email.REQUIRED),
    phone: Yup.string()
      .min(16, ValidMessages.inputs.phone.NOT_FULL)
      .required(ValidMessages.inputs.phone.REQUIRED),
  });

  const handleSubmit = async (values: typeof initialValues, { setSubmitting }: any) => {
    try {
      const result = await dispatch(
        fetchUpdateUserInfo({
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
        })
      ).unwrap();

      if (result.phoneChangeRequested && result.phoneChangeTo) {
        setPendingPhone(result.phoneChangeTo);
        setPendingFormValues({
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
        });
        setIsPhoneConfirmModalOpen(true);
      } else {
        setIsSuccessModalOpen(true);
        dispatch(fetchProfileInfo());
      }
    } catch (error: any) {
      console.error("Ошибка при обновлении данных:", error);
      // unwrap() отдаёт rejectWithValue строкой — в ней и текст про SMS-код при смене номера
      alert((typeof error === "string" ? error : error?.message) || "Ошибка при обновлении данных");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneConfirmSuccess = () => {
    setIsSuccessModalOpen(true);
    dispatch(fetchProfileInfo());
  };

  const handlePhoneResend = async () => {
    if (!pendingFormValues) return;
    await dispatch(
      fetchUpdateUserInfo(pendingFormValues)
    ).unwrap();
  };

  return (
    <div className={styles.wrapper}>
      {isMobile && (
        <div className={styles.headerMobile}>
          <button className={styles.logoutButton} onClick={handleBack}>
            <img src={arrowIcon} alt="back" />
          </button>
          <h2 className={styles.title}>
            Персональные данные
          </h2>
        </div>
      )}
      {!isMobile && (
        <h2 className={styles.title}>
          Персональные данные
        </h2>
      )}
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {(formik) => {
          const isDataChanged = 
            formik.values.name !== (profileInfo?.name || "") ||
            formik.values.email !== (profileInfo?.email || "") ||
            normalizePhone(formik.values.phone) !== normalizePhone(profileInfo?.phone);

          return (
            <Form className={styles.form}>
              <div className={styles.settingsList}>
                <div className={styles.settingsItem}>
                  <NameInput 
                    name="name" 
                    lable="ФИО" 
                    placeholder="Введите имя"
                  />
                  <ErrorMessage component="div" className={styles.errorMessage} name="name" />
                </div>

                <div className={styles.settingsItem}>
                  <EmailInput 
                    name="email" 
                    lable="Email" 
                    placeholder="Введите email"
                  />
                  <ErrorMessage component="div" className={styles.errorMessage} name="email" />
                </div>

                <div className={styles.settingsItem}>
                  <PhoneInput 
                    name="phone" 
                    lable="Телефон" 
                    placeholder="+7 (___) ___-__-__"
                  />
                  <ErrorMessage component="div" className={styles.errorMessage} name="phone" />
                </div>
              </div>

              <div className={styles.buttonsContainer}>
                <Button
                  type="submit"
                  disabled={!isDataChanged || formik.isSubmitting}
                  newStyles={styles.updateButton}
                >
                  {formik.isSubmitting ? "Обновление..." : "Сохранить изменения"}
                </Button>
                
                <Button
                  type="button"
                  onClickFunc={handleChangePass}
                  disabled={formik.isSubmitting}
                  newStyles={styles.changePassButton}
                >
                  Сменить пароль
                </Button>

                <div 
                  className={styles.deleteAccount}
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  Удалить аккаунт
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <ChangePasswordModal
        isOpen={isOpenPassModal}
        setIsOpen={setIsOpenPassModal}
      />

      <ConfirmPhoneModal
        isOpen={isPhoneConfirmModalOpen}
        setIsOpen={setIsPhoneConfirmModalOpen}
        phone={pendingPhone}
        onSuccess={handlePhoneConfirmSuccess}
        onResend={handlePhoneResend}
      />

      <SuccessSaveModal
        isOpen={isSuccessModalOpen}
        setIsOpen={setIsSuccessModalOpen}
      />

      <Modal
        title="Удаление аккаунта"
        subTitle="Вы уверены, что хотите удалить аккаунт?"
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        redTitle
        mobileContentSize="fixed"
      >
        <div className={styles.modalButtons}>
          <Button onClickFunc={() => setIsDeleteModalOpen(false)}>
            Нет
          </Button>
          <Button onClickFunc={handleDeleteAccount} newStyles={styles.deleteAccountButton}>
            Да
          </Button>
        </div>
      </Modal>
    </div>
  );
};
